<template>
  <div class="win-crit">
    <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
      <h2 class="h5 mb-0">Критерии победы</h2>
      <div class="d-flex flex-wrap gap-2 ms-auto">
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isEditing || aiLoading" @click="openQuiz">
          <i class="fa-solid fa-wand-magic-sparkles me-1"></i> Подобрать по тесту
        </button>
        <button type="button" class="btn btn-primary btn-sm" :disabled="isEditing || aiLoading" @click="startCreate">
          <i class="fa-solid fa-plus me-1"></i> Добавить критерий
        </button>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-hover align-middle">
        <thead class="table-light">
          <tr>
            <th style="width:60px" class="text-center">№</th>
            <th>Наименование критерия</th>
            <th>Описание критерия</th>
            <th style="width:220px" class="text-end">% критичности</th>
            <th style="width:160px" class="text-center">Действия</th>
          </tr>
        </thead>

        <tbody>
          <!-- строка создания / редактирования -->
          <tr v-if="isEditing">
            <td class="text-center">—</td>
            <td>
              <input v-model="form.name" class="form-control" placeholder="Например: Соответствие ТЗ" />
            </td>
            <td>
              <textarea v-model="form.description" class="form-control desc-editor" rows="3"
                placeholder="Краткое описание (что именно проверяем, на что смотрим)" @input="autoGrow"></textarea>
            </td>
            <td>
              <div class="input-group">
                <input v-model.number="form.criticality_percent" type="number" min="0" max="100"
                  class="form-control text-end" />
                <span class="input-group-text">%</span>
              </div>
            </td>
            <td class="text-center">
              <button class="btn btn-success btn-sm me-2" @click="save" title="Сохранить">
                <i class="fa-solid fa-check"></i>
              </button>
              <button class="btn btn-outline-secondary btn-sm" @click="cancel" title="Отмена">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </td>
          </tr>

          <!-- обычные строки -->
          <tr v-for="(row, idx) in rows" :key="row.id">
            <td class="text-center">{{ idx + 1 }}</td>
            <td class="name-cell">{{ row.name }}</td>
            <td class="desc-cell">{{ row.description || '—' }}</td>
            <td class="text-end fw-semibold">{{ row.criticality_percent }}%</td>
            <td class="text-center">
              <button class="btn btn-sm btn-outline-primary me-2" title="Редактировать" @click="startEdit(row)">
                <i class="fa-regular fa-pen-to-square"></i>
              </button>
              <button class="btn btn-sm btn-outline-danger" title="Удалить" @click="remove(row)">
                <i class="fa-solid fa-trash"></i>
              </button>
            </td>
          </tr>

          <tr v-if="!rows.length && !isEditing">
            <td colspan="5" class="text-center text-muted py-4">Пока пусто — добавьте первый критерий или пройдите тест.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="small text-muted">
      Всего: {{ rows.length }}
    </div>

    <TheModal :show="quizOpen" size="lg" :close-disabled="aiLoading" @close="closeQuiz">
      <div class="quiz-modal">
        <div v-if="aiLoading" class="quiz-loading-overlay">
          <div class="spinner-border text-primary mb-2" role="status" />
          <div class="fw-semibold">{{ quizLoadingTitle }}</div>
          <div class="small text-muted mt-1">
            Обычно 10–30 секунд, иногда до 2–3 минут. Не закрывайте окно.
          </div>
          <div v-if="quizElapsedSeconds > 0" class="small text-muted mt-1">
            Прошло {{ quizElapsedSeconds }} сек.
          </div>
          <div v-if="quizSaveTotal > 0" class="small text-muted mt-1">
            Сохранено {{ quizSaveDone }} из {{ quizSaveTotal }}
          </div>
        </div>
        <h5 class="mb-2">Тест: критерии победы</h5>
        <p class="text-muted small mb-3">
          Ответьте на вопросы — результат уйдёт в AI-сервис и будет преобразован в таблицу критериев (как в личном кабинете).
        </p>
        <div v-if="quizError" class="alert alert-danger py-2 small">{{ quizError }}</div>
        <div class="quiz-scroll vstack gap-3 mb-3">
          <div v-for="item in QUIZ_ITEMS" :key="item.id" class="quiz-item">
            <label class="form-label fw-semibold mb-1" :for="'qc-' + item.id">{{ item.question }}</label>
            <textarea
              :id="'qc-' + item.id"
              v-model="quizDraft[item.id]"
              class="form-control"
              rows="3"
              :placeholder="item.placeholder"
            />
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" :disabled="aiLoading" @click="closeQuiz">Отмена</button>
          <button type="button" class="btn btn-primary" :disabled="aiLoading" @click="submitQuiz">
            <span v-if="aiLoading" class="spinner-border spinner-border-sm me-1" role="status" />
            Сгенерировать критерии
          </button>
        </div>
      </div>
    </TheModal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, watch, defineExpose } from 'vue'
import api from '../api'
import TheModal from './TheModal.vue'
import { invalidateWinCriteriaCache } from '@/utils/winCriteriaGate'
import { WIN_CRITERIA_QUIZ_ITEMS as QUIZ_ITEMS } from '@/constants/setupQuiz'
import {
  collectQuizAnswers,
  validateQuizRequired,
  submitWinCriteriaFromQuiz,
  quizErrorMessage,
} from '@/composables/useWinCriteriaQuiz'

const rows = ref([])
const isEditing = ref(false)
const editingId = ref(null)
const form = ref({
  name: '',
  description: '',
  criticality_percent: 0,
})

const quizOpen = ref(false)
const quizDraft = reactive({})
const quizError = ref('')
const aiLoading = ref(false)
/** «generating» — запрос к AI; «saving» — поочерёдное POST в ЛК */
const quizLoadPhase = ref('idle')
const quizSaveDone = ref(0)
const quizSaveTotal = ref(0)
const quizElapsedSeconds = ref(0)
let quizElapsedTimer = null
let quizRequestGen = 0

function clearQuizElapsedTimer() {
  if (quizElapsedTimer) {
    clearInterval(quizElapsedTimer)
    quizElapsedTimer = null
  }
  quizElapsedSeconds.value = 0
}

watch(aiLoading, (loading) => {
  clearQuizElapsedTimer()
  if (!loading) return
  quizElapsedSeconds.value = 0
  quizElapsedTimer = setInterval(() => {
    quizElapsedSeconds.value += 1
  }, 1000)
})

onUnmounted(() => {
  clearQuizElapsedTimer()
})

const quizLoadingTitle = computed(() => {
  if (quizLoadPhase.value === 'saving') return 'Сохраняем критерии в личный кабинет…'
  return 'Генерируем критерии, подождите…'
})

function resetQuizDraft() {
  for (const item of QUIZ_ITEMS) {
    quizDraft[item.id] = ''
  }
}

async function load() {
  const { data } = await api.get('/win-criteria/')
  rows.value = data
  invalidateWinCriteriaCache()
}

function openQuiz() {
  quizError.value = ''
  resetQuizDraft()
  quizOpen.value = true
}

function closeQuiz() {
  if (aiLoading.value) {
    const ok = window.confirm('Подбор критериев ещё идёт. Прервать?')
    if (!ok) return
    quizRequestGen += 1
    aiLoading.value = false
    quizLoadPhase.value = 'idle'
    quizSaveDone.value = 0
    quizSaveTotal.value = 0
  }
  quizOpen.value = false
  quizError.value = ''
}

defineExpose({
  isQuizBusy: () => aiLoading.value,
})

function startCreate() {
  isEditing.value = true
  editingId.value = null
  form.value = { name: '', description: '', criticality_percent: 0 }
  requestAnimationFrame(() => {
    const ta = document.querySelector('.desc-editor')
    if (ta) autoGrow({ target: ta })
  })
}

function startEdit(row) {
  isEditing.value = true
  editingId.value = row.id
  form.value = {
    name: row.name,
    description: row.description || '',
    criticality_percent: row.criticality_percent ?? 0,
  }
  requestAnimationFrame(() => {
    const ta = document.querySelector('.desc-editor')
    if (ta) autoGrow({ target: ta })
  })
}

function cancel() {
  isEditing.value = false
  editingId.value = null
}

function autoGrow(e) {
  const el = e.target
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 400) + 'px'
}

async function save() {
  const payload = {
    name: (form.value.name || '').trim(),
    description: (form.value.description || '').trim(),
    criticality_percent: Number(form.value.criticality_percent) || 0,
  }
  if (!payload.name) return alert('Введите наименование критерия')
  if (payload.criticality_percent < 0 || payload.criticality_percent > 100) {
    return alert('Процент критичности должен быть в диапазоне 0–100')
  }

  if (editingId.value) {
    await api.patch(`/win-criteria/${editingId.value}/`, payload)
  } else {
    await api.post('/win-criteria/', payload)
  }
  await load()
  cancel()
}

async function remove(row) {
  if (!confirm(`Удалить критерий «${row.name}»?`)) return
  await api.delete(`/win-criteria/${row.id}/`)
  await load()
}

async function submitQuiz() {
  quizError.value = ''
  const answers = collectQuizAnswers(QUIZ_ITEMS, quizDraft)
  const requiredEmpty = validateQuizRequired(QUIZ_ITEMS, quizDraft)
  if (requiredEmpty.length) {
    quizError.value = 'Ответьте на все обязательные вопросы (кроме блока «Дополнительно»).'
    return
  }

  aiLoading.value = true
  quizLoadPhase.value = 'generating'
  quizSaveDone.value = 0
  quizSaveTotal.value = 0
  const requestGen = ++quizRequestGen
  try {
    quizLoadPhase.value = 'saving'
    const result = await submitWinCriteriaFromQuiz(answers, {
      onSaveProgress: ({ done, total }) => {
        if (requestGen !== quizRequestGen) return
        quizSaveDone.value = done
        quizSaveTotal.value = total
      },
    })
    if (requestGen !== quizRequestGen) return
    if (!result.ok) {
      quizError.value = result.error
      quizLoadPhase.value = 'generating'
      return
    }
    await load()
    quizOpen.value = false
    quizError.value = ''
  } catch (e) {
    if (requestGen !== quizRequestGen) return
    quizError.value = quizErrorMessage(e)
  } finally {
    if (requestGen === quizRequestGen) {
      aiLoading.value = false
      quizLoadPhase.value = 'idle'
      quizSaveDone.value = 0
      quizSaveTotal.value = 0
    }
  }
}

onMounted(() => {
  resetQuizDraft()
  load()
})
</script>

<style scoped>
.win-crit {
  font-size: 0.98rem;
}

.win-crit .table thead th {
  vertical-align: middle;
}

.win-crit .table td,
.win-crit .table th {
  vertical-align: top;
}

.name-cell {
  font-weight: 600;
}

.desc-cell {
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
  white-space: pre-wrap;
  word-break: break-word;
}

.desc-editor {
  min-height: 96px;
  font-size: 1rem;
  line-height: 1.6;
  resize: vertical;
}

.win-crit .btn-sm {
  border-radius: 10px;
}

.quiz-scroll {
  max-height: min(60vh, 520px);
  overflow-y: auto;
}

.quiz-item textarea {
  resize: vertical;
}

.quiz-modal {
  position: relative;
  min-height: 120px;
}

.quiz-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.88);
  border-radius: 0.375rem;
}
</style>
